import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../contexts/AuthContext';
import { ArrowLeft, AlertCircle, Check, ChevronRight, Sparkles } from '../components/Icons';
import { type Currency, formatPrice } from '../lib/currency';

type EditStep = 'view' | 'edit';

interface CreditsResponse {
  balance: string;
  planMonthlyCredits: number | null;
  planName: string | null;
  freeTierCredits: number;
  totalGranted: number;
}
interface DisplayPrice { INR: string; USD: string; }
interface StarterSku { credits: string; displayPrice: DisplayPrice; }

function Profile() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [step, setStep]     = useState<EditStep>('view');
  const [name, setName]     = useState(user?.businessName || '');
  const [mobile, setMobile] = useState(user?.mobile || '');
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');
  const [success, setSuccess] = useState(false);
  const [creditsBalance, setCreditsBalance]         = useState<number | null>(null);
  const [planMonthlyCredits, setPlanMonthlyCredits] = useState<number | null>(null);
  const [planName, setPlanName]                     = useState<string | null>(null);
  const [totalGranted, setTotalGranted]             = useState<number>(0);
  const [starterSku, setStarterSku]                 = useState<StarterSku | null>(null);
  const [currency, setCurrencyState]                = useState<Currency>('INR');

  useEffect(() => {
    Promise.all([
      axios.get<CreditsResponse>(`${API}/credits`),
      axios.get<{ currency: Currency | null; skus: (StarterSku & { id: string; kind: string })[] }>(`${API}/credits/pricing`),
    ]).then(([cRes, pRes]) => {
      setCreditsBalance(Number(cRes.data.balance));
      setPlanMonthlyCredits(cRes.data.planMonthlyCredits);
      setPlanName(cRes.data.planName);
      setTotalGranted(cRes.data.totalGranted);
      if (pRes.data.currency) setCurrencyState(pRes.data.currency);
      const sku = pRes.data.skus.find(s => s.id === 'starter_monthly');
      if (sku) setStarterSku(sku);
    }).catch(() => {}); // fail silently — credits row shows '—' on error
  }, []);

  const handleLogout = () => { logout(); navigate('/', { replace: true }); };

  /* ── Save profile changes ───────────────────────────────────── */
  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Name is required'); return; }
    if (!mobile.trim()) { setError('Mobile number is required'); return; }
    setError('');
    setLoading(true);
    try {
      await axios.put(`${API}/user/profile`, { businessName: name.trim(), mobile: mobile.trim() });
      setSuccess(true);
      setStep('view');
      setTimeout(() => setSuccess(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save profile. Try again.');
    } finally { setLoading(false); }
  };

  const initials = (user?.businessName || 'U').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);

  return (
    <div className="page-root">

      {/* Header */}
      <header className="app-header">
        <div className="header-inner">
          <button className="header-action-btn" onClick={() => navigate('/home')} aria-label="Back to home">
            <ArrowLeft size={20} />
          </button>
          <div style={{ flex: 1 }} />
        </div>
      </header>

      <div className="page-scroll">
        <div className="page-container" style={{ paddingTop: 24, paddingBottom: 48 }}>

          {/* Avatar */}
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
            <div className="profile-avatar">{initials}</div>
            <h2 className="heading-md" style={{ marginTop: 12 }}>{user?.businessName || 'User'}</h2>
            <p className="body-sm" style={{ color: 'var(--text-faint)', marginTop: 2 }}>{user?.mobile ? `+91 ${user.mobile}` : ''}</p>
          </div>

          {/* Success banner */}
          {success && (
            <div className="alert" style={{ background: 'var(--success-bg)', borderColor: 'var(--success)', color: 'var(--success)', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Check size={16} /><span>Profile updated successfully</span>
            </div>
          )}

          {/* ── VIEW mode ──────────────────────────────────────── */}
          {step === 'view' && (
            <>
              {/* Credit consumption bar */}
              {creditsBalance !== null && (() => {
                // Subscription users: denominator = plan monthly allotment.
                // Non-subscription users: denominator = total credits ever granted
                // (registration bonus + admin adjustments + pack purchases).
                // This correctly handles admin-credited free-tier users without
                // breaking the bar, and is configurable via backend env.
                const total      = planMonthlyCredits ?? Math.max(totalGranted, creditsBalance);
                const used       = Math.max(0, total - creditsBalance);
                const fillPct    = total > 0 ? Math.max(0, Math.min(100, (used / total) * 100)) : 0;
                const isLow      = total > 0 && creditsBalance / total <= 0.2;
                const badgeLabel = planName
                  ? planName.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                  : 'Free plan';
                const totalLabel = planMonthlyCredits ? `/ ${total} credits/mo` : `/ ${total} credits`;
                return (
                  <div className="credit-bar-card" style={{ marginBottom: 14 }}>
                    <div className="credit-bar-head">
                      <div>
                        <span className="credit-bar-numerator">{creditsBalance}</span>
                        <span className="credit-bar-denominator"> {totalLabel}</span>
                      </div>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center',
                        padding: '3px 8px', borderRadius: 999,
                        background: 'var(--accent-light)', color: 'var(--accent-dark)',
                        fontSize: 11, fontWeight: 600,
                      }}>{badgeLabel}</span>
                    </div>
                    <div className="credit-bar-track">
                      <div
                        className={`credit-bar-fill${isLow ? ' credit-bar-fill-low' : ''}`}
                        style={{ width: `${fillPct}%` }}
                      />
                    </div>
                    <div className="credit-bar-meta">
                      <span><strong>{used}</strong> used</span>
                      <span><strong>{creditsBalance}</strong> remaining</span>
                    </div>
                  </div>
                );
              })()}

              {/* Upgrade card — only shown when not on a paid subscription */}
              {!planName && (
                <div className="upgrade-card" style={{ marginBottom: 14 }}>
                  <div className="upgrade-card-eyebrow">⚡ Upgrade</div>
                  <h4 className="upgrade-card-title">Get more credits</h4>
                  {starterSku && (
                    <p className="upgrade-card-sub">
                      Starter · {formatPrice(starterSku.displayPrice[currency], currency)}/mo · {starterSku.credits} credits/mo
                    </p>
                  )}
                  <button
                    className="upgrade-card-btn"
                    onClick={() => navigate('/credits')}
                    type="button"
                  >
                    View plans →
                  </button>
                </div>
              )}

              <div className="profile-fields">
                <ProfileField label="Full Name"    value={user?.businessName || '—'} />
                <ProfileField label="Mobile"       value={mobile || '—'} />
                <ProfileField label="Email"        value={user?.email || '—'} />
              </div>

              <button
                type="button"
                className="profile-nav-row"
                onClick={() => navigate('/transactions')}
              >
                <span className="profile-nav-label">Transaction History</span>
                <ChevronRight size={16} style={{ color: 'var(--text-faint)' }} />
              </button>

              <button className="btn btn-primary" style={{ width: '100%', marginTop: 24 }} onClick={() => { setStep('edit'); setError(''); }}>
                Edit Profile
              </button>
              <button className="btn btn-secondary" style={{ width: '100%', marginTop: 10 }} onClick={handleLogout}>
                Sign Out
              </button>
            </>
          )}

          {/* ── EDIT mode ──────────────────────────────────────── */}
          {step === 'edit' && (
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: 16 }} noValidate>
              {error && (
                <div className="alert alert-error" role="alert">
                  <AlertCircle size={16} style={{ flexShrink: 0 }} /><span>{error}</span>
                </div>
              )}

              <div className="form-field">
                <label className="form-label" htmlFor="prof-name">Full Name<span className="required">*</span></label>
                <input id="prof-name" className="form-input" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" required />
              </div>

              <div className="form-field">
                <label className="form-label" htmlFor="prof-mobile">Mobile<span className="required">*</span></label>
                <input
                  id="prof-mobile"
                  className="form-input"
                  type="tel"
                  inputMode="tel"
                  value={mobile}
                  onChange={e => setMobile(e.target.value)}
                  placeholder="+91XXXXXXXXXX"
                  required
                />
              </div>

              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => { setStep('view'); setError(''); }}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 2 }} disabled={loading}>
                  {loading ? <><span className="spinner" style={{ width: 16, height: 16, borderWidth: 2 }} /> Saving…</> : 'Save Changes'}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>

    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="profile-field-row">
      <span className="profile-field-label">{label}</span>
      <span className="profile-field-value">{value}</span>
    </div>
  );
}

export default Profile;
